import { LibraryCache } from 'base/library_cache';
import { CardStyle, TypeSettingsState, UiStyle } from 'base/styleguide/model';
import { CompiledScheme } from 'base/styleguide/types';
import { BlocksService } from 'blocks/blocks_service';
import { ElementView } from 'blocks/component_renderer/element_view';
import { observer } from 'mobx-react-lite';
import React, { CSSProperties, useEffect } from 'react';
import { ConceptAPI } from './concept_store';
import { FONT_WEIGHTS_BODY, globalRemoteFontStore } from './fonts';
import { ImageOverridesCss } from './image_styles';
import './scheme-override.global.css';
import { useConceptStyleGuideVariables } from './style_application/concept_styleguide';
import {
  compiledSchemeVariables,
  hackedSchemeVariables,
} from './style_application/generate_schemes';
import {
  generateTypeSettingsCSSVariables,
  HEADING_FONT_WEIGHTS,
} from './type_settings';
import { UiStyleCss } from './ui_styles';

type ElementViewProps = React.ComponentProps<typeof ElementView>;

type StyledElementViewProps = Omit<
  ElementViewProps,
  'concept' | 'scheme' | 'omitStyleGuide'
> & {
  concept: ConceptAPI;
  scheme: CompiledScheme;
};

export const StyledElementView = ({
  concept,
  scheme,
  ...passthrough
}: StyledElementViewProps) => {
  return (
    <div
      className="is-scheme-override"
      style={{
        ...[
          ...Object.entries(scheme ? compiledSchemeVariables(scheme) : {}),
        ].reduce<CSSProperties>(
          (acc, [name, value]) => ({
            ...acc,
            [`--${name}`]: `var(--${value})`,
            [`--u-relume-variable--${name}`]: `var(--${value})`,
          }),
          {},
        ),
        ...hackedSchemeVariables(scheme, concept),
        display: 'contents',
      }}
      data-concept-id={concept.state.uuid}
    >
      <ElementView
        {...passthrough}
        concept={concept}
        scheme={scheme}
        omitStyleGuide
      />
    </div>
  );
};

type Overrides = {
  headingFontFamilyId?: string;
  bodyFontFamilyId?: string;
  typeSettings?: Partial<TypeSettingsState>;
  uiStyle?: UiStyle;
  cardStyle?: CardStyle;
};

const getConceptValuesWithOverrides = (
  concept: ConceptAPI,
  overrides: Overrides,
) => ({
  headingFontFamilyId:
    overrides.headingFontFamilyId ?? concept.state.fontFamilies.heading.slug,
  bodyFontFamilyId:
    overrides.bodyFontFamilyId ?? concept.state.fontFamilies.body.slug,
  headingFontWeight:
    HEADING_FONT_WEIGHTS[
      overrides.typeSettings?.weight ?? concept.state.typeSettings.weight
    ],
  typeSettings: {
    ...concept.state.typeSettings,
    ...overrides.typeSettings,
  },
  uiStyle: overrides.uiStyle ?? concept.uiStyle,
  cardStyle: overrides.cardStyle ?? concept.cardStyle,
});

export const StyledElementsContext = observer(function _StyledElementsContext({
  concept,
  library,
  children,
  overrides = {},
}: {
  concept: ConceptAPI;
  library: LibraryCache;
  children: React.ReactNode;
  overrides?: Overrides;
}) {
  const {
    headingFontFamilyId,
    bodyFontFamilyId,
    headingFontWeight,
    typeSettings,
    uiStyle,
    cardStyle,
  } = getConceptValuesWithOverrides(concept, overrides);

  // Ensure that font family variables are only changed when all fonts are loaded.
  const fontFamilyVariableValuesRef = React.useRef<Record<string, string>>({});
  if (
    headingFontFamilyId &&
    bodyFontFamilyId &&
    globalRemoteFontStore.isRemoteFontFamilyLoaded(headingFontFamilyId, [
      headingFontWeight,
    ]) &&
    globalRemoteFontStore.isRemoteFontFamilyLoaded(
      bodyFontFamilyId,
      FONT_WEIGHTS_BODY,
    )
  ) {
    fontFamilyVariableValuesRef.current = {
      'heading-font-family': headingFontFamilyId,
      'body-font-family': bodyFontFamilyId,
    };
  } else {
    fontFamilyVariableValuesRef.current = {
      ...fontFamilyVariableValuesRef.current,
      ...(headingFontFamilyId !==
        fontFamilyVariableValuesRef.current['heading-font-family'] && {
        'heading-opacity': '0.5',
      }),
      ...(bodyFontFamilyId !==
        fontFamilyVariableValuesRef.current['body-font-family'] && {
        'body-opacity': '0.5',
      }),
    };
  }

  const typeSettingsVariables = generateTypeSettingsCSSVariables(typeSettings);

  return (
    <div
      style={{
        ...useConceptStyleGuideVariables(concept),
        ...[
          ...Object.entries(fontFamilyVariableValuesRef.current),
          ...Object.entries(typeSettingsVariables),
        ].reduce<CSSProperties>(
          (acc, [name, value]) => ({
            ...acc,
            [`--${name}`]: value,
          }),
          {},
        ),
        display: 'contents',
      }}
    >
      <ImageOverridesCss concept={concept} />
      <UiStyleCss
        rootClassName={`is-scheme-override[data-concept-id="${concept.state.uuid}"]`}
        library={library}
        uiStyle={uiStyle}
        cardStyle={cardStyle}
        radiusOption={concept.uiStyleRadiusOption}
        concept={concept}
      />
      {children}
    </div>
  );
});

export const useLoadConceptFonts = ({
  blocksService,
  concept,
  overrides = {},
}: {
  blocksService: BlocksService;
  concept: ConceptAPI;
  overrides?: Overrides;
}) => {
  const { headingFontFamilyId, bodyFontFamilyId, headingFontWeight } =
    getConceptValuesWithOverrides(concept, overrides);

  useEffect(() => {
    if (!headingFontFamilyId) {
      return;
    }
    globalRemoteFontStore.loadRemoteFontFamily(
      headingFontFamilyId,
      [headingFontWeight],
      {
        blocksService,
      },
    );
  }, [blocksService, headingFontFamilyId, headingFontWeight]);

  useEffect(() => {
    if (!bodyFontFamilyId) {
      return;
    }
    globalRemoteFontStore.loadRemoteFontFamily(
      bodyFontFamilyId,
      FONT_WEIGHTS_BODY,
      {
        blocksService,
      },
    );
  }, [blocksService, bodyFontFamilyId]);
};
